import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import { track } from '../utils/track';

// Only same-site paths for ?next — "//evil.com" would be an open redirect.
const safeNext = (v) => (typeof v === 'string' && v.startsWith('/') && !v.startsWith('//') ? v : null);

export default function LoginPage() {
  useSEO({ title: 'Sign In | Steady Education', url: 'https://steadyeducation.com/login' });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = safeNext(new URLSearchParams(location.search).get('next'));
  const from = location.state?.from || next || '/my-courses';

  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const user = await login(form.email, form.password);
      track('login');
      navigate(user.role === 'admin' ? '/admin' : from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <Link to="/" style={styles.logoLink}>
          <div style={styles.logoMark}>SE</div>
          <div>
            <div style={styles.logoName}>Steady Education</div>
            <div style={styles.logoSub}>Arizona Real Estate CE</div>
          </div>
        </Link>

        <h1 style={styles.title}>Welcome back</h1>
        <p style={styles.sub}>Sign in to continue your CE courses</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="input" type="password" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
          </div>
          <div style={styles.forgotRow}>
            <Link to="/forgot-password" style={styles.forgotLink}>Forgot password?</Link>
          </div>
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={styles.registerLink}>
          Don't have an account? <Link to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}>Create one</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--slate-50)', padding: 20 },
  card: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 20, padding: '40px', width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 20, boxShadow: 'var(--shadow-lg)' },
  logoLink: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', marginBottom: 8 },
  logoMark: { width: 42, height: 42, background: 'linear-gradient(135deg, #0d9488, #0f766e)', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 15 },
  logoName: { fontSize: 16, fontWeight: 700, color: 'var(--slate-900)' },
  logoSub: { fontSize: 11, color: 'var(--color-text-muted)' },
  title: { fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--slate-900)' },
  sub: { fontSize: 15, color: 'var(--color-text-muted)', marginTop: -12 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  forgotRow: { textAlign: 'right', marginTop: -8 },
  forgotLink: { fontSize: 13, color: 'var(--color-primary)' },
  registerLink: { fontSize: 14, color: 'var(--color-text-muted)', textAlign: 'center' },
};
